import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/lib/supabase";
import { ImageBox } from "@/components/ui-extras";
import { AppShell } from "@/components/app-shell";
import { BG_PREFERENCE_SELECT, bgFields, resolvePreferredPhoto } from "@/lib/photo-preference";

export const Route = createFileRoute("/favorites")({
  component: () => (
    <AppShell>
      <FavoritesPage />
    </AppShell>
  ),
});

type FavCard = {
  kind: "jewelry" | "clothing" | "accessory";
  id: string;
  name: string;
  photo_url: string | null;
  category: string | null;
  photo_url_no_bg: string | null;
  prefer_clean_bg: boolean | null;
  clean_bg_color: string | null;
};

function FavoritesPage() {
  const { user } = useAuth();
  const uid = user?.id;
  const router = useRouter();

  const { data: favorites = [], isLoading } = useQuery({
    queryKey: ["favorites_all", uid],
    enabled: !!uid,
    queryFn: async () => {
      const [j, c, a] = await Promise.all([
        supabase
          .from("jewelry_items")
          .select(`id, name, photo_url, category, ${BG_PREFERENCE_SELECT}`)
          .eq("user_id", uid!)
          .eq("is_favorite", true)
          .eq("is_wishlist", false)
          .eq("is_accessory", false)
          .eq("is_archived", false)
          .order("favorited_at", { ascending: false }),
        supabase
          .from("clothing_items")
          .select(`id, name, photo_url, clothing_type, ${BG_PREFERENCE_SELECT}`)
          .eq("user_id", uid!)
          .eq("is_favorite", true)
          .eq("is_wishlist", false)
          .eq("is_archived", false)
          .order("favorited_at", { ascending: false }),
        supabase
          .from("jewelry_items")
          .select(`id, name, photo_url, accessory_type, ${BG_PREFERENCE_SELECT}`)
          .eq("user_id", uid!)
          .eq("is_favorite", true)
          .eq("is_wishlist", false)
          .eq("is_accessory", true)
          .eq("is_archived", false)
          .order("favorited_at", { ascending: false }),
      ]);
      const cards: FavCard[] = [
        ...((j.data ?? []) as Array<{ id: string; name: string; photo_url: string | null; category: string | null } & Record<string, unknown>>).map(
          (i) => ({ kind: "jewelry" as const, id: i.id, name: i.name, photo_url: i.photo_url, category: i.category, ...bgFields(i) }),
        ),
        ...((c.data ?? []) as Array<{ id: string; name: string; photo_url: string | null; clothing_type: string | null } & Record<string, unknown>>).map(
          (i) => ({ kind: "clothing" as const, id: i.id, name: i.name, photo_url: i.photo_url, category: i.clothing_type, ...bgFields(i) }),
        ),
        ...((a.data ?? []) as Array<{ id: string; name: string; photo_url: string | null; accessory_type: string | null } & Record<string, unknown>>).map(
          (i) => ({ kind: "accessory" as const, id: i.id, name: i.name, photo_url: i.photo_url, category: i.accessory_type, ...bgFields(i) }),
        ),
      ];
      return cards;
    },
  });

  const routeFor = (kind: FavCard["kind"]) =>
    kind === "jewelry"
      ? "/jewelry/$id"
      : kind === "clothing"
        ? "/clothing/$id"
        : "/accessories/$id";

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--caileve-bg)" }}>
      <div className="max-w-7xl mx-auto px-8 pt-8 pb-6 flex items-start gap-4">
        <button
          type="button"
          onClick={() => router.history.back()}
          aria-label="Back"
          className="mt-1 shrink-0"
          style={{ color: "var(--caileve-ink)" }}
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1
            className="font-serif-display text-3xl"
            style={{ color: "var(--caileve-lilac-text)" }}
          >
            <span style={{ color: "var(--caileve-gold-text)", marginRight: 8 }}>✦</span>
            Most Reached For
          </h1>
          <p
            className="italic text-base mt-1"
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              color: "var(--caileve-muted)",
            }}
          >
            The pieces you return to.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 pb-16">
        {isLoading ? (
          <p
            className="italic text-lg py-8"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-muted)" }}
          >
            Finding your most loved pieces…
          </p>
        ) : favorites.length === 0 ? (
          <p
            className="italic text-lg py-8"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-muted)" }}
          >
            Mark a piece as a favourite from your collection to see it here.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {favorites.map((item) => {
              const resolved = resolvePreferredPhoto(item);
              return (
              <Link
                key={`${item.kind}-${item.id}`}
                to={routeFor(item.kind)}
                params={{ id: item.id }}
                className="group block"
              >
                <ImageBox src={resolved.src} alt={item.name} style={resolved.backdropStyle} />
                <p className="font-serif-display text-lg mt-3 leading-tight text-[var(--caileve-ink)]">
                  {item.name}
                </p>
                <p
                  className="text-[10px] uppercase mt-1 pb-3"
                  style={{ letterSpacing: "0.25em", color: "var(--caileve-muted)" }}
                >
                  {item.category ?? item.kind}
                </p>
              </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
